import { useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Terminal, Mail, Lock, User, Loader2, ArrowRight } from "lucide-react";

const AuthPage = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { login, register } = useAuth();
  
  const [isRegister, setIsRegister] = useState(searchParams.get("register") === "true");
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
  });

  const from = location.state?.from || "/dashboard";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isRegister) {
        if (!formData.name) {
          toast.error("Veuillez entrer votre nom");
          setLoading(false);
          return;
        }
        await register(formData.email, formData.password, formData.name);
        toast.success("Compte créé avec succès!");
      } else {
        await login(formData.email, formData.password);
        toast.success("Connexion réussie!");
      }
      navigate(from, { replace: true });
    } catch (error) {
      console.error("Auth error:", error);
      toast.error(error.response?.data?.detail || "Erreur d'authentification");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex" data-testid="auth-page">
      {/* Left Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex items-center gap-2 mb-8">
            <Terminal className="h-8 w-8 text-primary" />
            <span className="font-heading font-bold text-xl">
              DevOps<span className="text-primary">Learn</span>
            </span>
          </div>

          <h1 className="text-2xl font-heading font-bold mb-2">
            {isRegister ? "Créer un compte" : "Bienvenue"}
          </h1>
          <p className="text-muted-foreground mb-8">
            {isRegister 
              ? "Commencez votre parcours DevOps aujourd'hui" 
              : "Connectez-vous pour accéder à vos cours"
            }
          </p>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {isRegister && (
              <div>
                <Label htmlFor="name">Nom complet</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="name"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="pl-10"
                    data-testid="name-input"
                  />
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="email">Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="pl-10"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-10"
                  required
                  minLength={6}
                  data-testid="password-input"
                />
              </div>
            </div>

            <Button type="submit" className="w-full glow-green" disabled={loading} data-testid="submit-btn">
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  {isRegister ? "Créer mon compte" : "Se connecter"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground mt-6">
            {isRegister ? "Déjà un compte?" : "Pas encore de compte?"}{" "}
            <button
              onClick={() => setIsRegister(!isRegister)}
              className="text-primary hover:underline font-medium"
              data-testid="toggle-auth-mode"
            >
              {isRegister ? "Se connecter" : "S'inscrire"}
            </button>
          </p>
        </div>
      </div>

      {/* Right Panel - Visual */}
      <div className="hidden lg:flex flex-1 bg-card/50 border-l border-border items-center justify-center p-8">
        <div className="max-w-md">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 to-secondary/20 rounded-lg blur-xl opacity-50"></div>
            <div className="relative terminal rounded-lg overflow-hidden border border-border">
              <div className="bg-[#1a1a1a] px-4 py-2 flex items-center gap-2 border-b border-[#333]">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-[#ff5f56]"></div>
                  <div className="w-3 h-3 rounded-full bg-[#ffbd2e]"></div>
                  <div className="w-3 h-3 rounded-full bg-[#27ca3f]"></div>
                </div>
                <span className="text-xs text-gray-400 ml-2 font-mono">terminal</span>
              </div>
              <div className="p-4 font-mono text-sm" style={{ background: "#0a0a0a" }}>
                <div className="text-green-400 mb-2">$ echo "Welcome to DevOpsLearn"</div>
                <div className="text-gray-300 mb-3">Welcome to DevOpsLearn</div>
                <div className="text-green-400 mb-2">$ ./start-learning.sh</div>
                <div className="text-cyan-400 mb-1">Loading courses...</div>
                <div className="text-cyan-400 mb-1">Setting up labs...</div>
                <div className="text-yellow-400 mb-3">Ready to learn! 🚀</div>
                <div className="flex items-center text-green-400">
                  <span>$</span>
                  <span className="w-2 h-4 bg-green-400 ml-1 cursor-blink"></span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 text-center">
            <h2 className="text-xl font-heading font-bold mb-2">
              Apprenez en pratiquant
            </h2>
            <p className="text-muted-foreground">
              Accédez à des environnements Cloud réels et maîtrisez Docker, Kubernetes, AWS et plus encore.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
